import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useLongLoad } from 'reactApp/hooks/useLongLoad';
import { EStorageType } from 'reactApp/modules/storage/storage.types';

import { SearchTooltip } from '../SearchTooltip/SearchTooltip';
import { SearchTooltipTypes } from '../SearchTooltip/SearchTooltip.data';
import { getSearchFilterTooltipType, getSearchTooltipType } from '../SearchTooltip/SearchTooltip.helpers';
import { useTooltipState } from '../SearchTooltip/SearchTooltip.hooks';
import { CategoryFilters } from './CategoryFilters/CategoryFilters';
import { ClearButton } from './ClearButton/ClearButton';
import { useWebSearchContent } from './hooks/useWebSearchContent';
import { SearchButton } from './SearchButton/SearchButton';
import { SearchInput } from './SearchInput/SearchInput';
import { TransitionWrapper } from './TransitionWrapper/TransitionWrapper';
import { TypeSelectButton } from './TypeSelectButton/TypeSelectButton';

interface IProps {
    dropdownAvailable: boolean;
    toggleSearch: (state: boolean) => void;
    isExpanded: boolean;
    onlyExpanded?: boolean;
    storage: EStorageType;
}

export const WebSearch = memo(({ dropdownAvailable, toggleSearch, isExpanded, storage, onlyExpanded = false }: IProps) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const categoryFilersRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // todo: необходимо для корректного отображения тултипов, после окончания эксперимента можно удалить!
    const [isSearcgFullyExpanded, setSearchFullyExpanded] = useState(isExpanded || onlyExpanded);

    const searchTooltipType = getSearchTooltipType();
    const searchFilterTooltipType = getSearchFilterTooltipType();

    const { isTooltipVisible: isSearchTooltipVisible, onTooltipClose: onSearchTooltipClose } = useTooltipState({
        visibilityValue: !!(searchTooltipType && searchInputRef?.current && !isSearcgFullyExpanded && !(isExpanded || onlyExpanded)),
        tooltipType: searchTooltipType,
    });
    const { isTooltipVisible: isSearchFilterTooltipVisible, onTooltipClose: onSearchFilterTooltipClose } = useTooltipState({
        visibilityValue: !!(searchFilterTooltipType && (isExpanded || onlyExpanded) && categoryFilersRef),
        tooltipType: searchFilterTooltipType,
    });

    const onSearchStateChange = ({ type, payload }: { type: string; payload?: any }) => {
        if (type === 'setActive') {
            onSearchTooltipClose();
        }
        if (type === 'toggleFilters' || type === 'setValue' || (type === 'setFocus' && payload)) {
            onSearchFilterTooltipClose();
        }
    };

    const onTransitionEntered = useCallback(() => {
        setActiveInput();
        setSearchFullyExpanded(true);
    }, []);

    const onTransitionExited = useCallback(() => setSearchFullyExpanded(false), []);

    const {
        isOpen,
        value,
        searchOption,
        placeholder,
        onSearchClick,
        onInputClick,
        onDocumentClick,
        onInputChange,
        onInputClear,
        onKeyUp,
        onToggleFilters,
        setActiveInput,
        chooseSearchType,
        removeSearchType,
    } = useWebSearchContent({ toggleSearch, rootRef, isExpanded, onlyExpanded, storage, dropdownRef, onSearchStateChange });
    const { longLoad } = useLongLoad();

    useEffect(() => {
        document.addEventListener('click', onDocumentClick, { capture: true });

        return () => {
            document.removeEventListener('click', onDocumentClick, { capture: true });
        };
    }, [onDocumentClick]);

    return (
        <TransitionWrapper ref={rootRef} isExpanded={isExpanded} onEntered={onTransitionEntered} onExited={onTransitionExited}>
            <SearchInput
                placeholder={placeholder}
                onClick={onInputClick}
                onChange={onInputChange}
                value={value}
                onKeyUp={onKeyUp}
                getRef={searchInputRef}
                after={
                    isExpanded && (
                        <>
                            <ClearButton onClick={onInputClear} />
                            {dropdownAvailable && (
                                <TypeSelectButton
                                    searchOption={searchOption}
                                    onClick={onToggleFilters}
                                    ref={categoryFilersRef}
                                    isOpen={isOpen}
                                />
                            )}
                            <SearchButton loading={longLoad} onClick={onSearchClick} />
                        </>
                    )
                }
            />
            {isSearchTooltipVisible && searchInputRef?.current && (
                <SearchTooltip
                    targetRef={searchInputRef.current}
                    type={searchTooltipType as SearchTooltipTypes}
                    onClose={onSearchTooltipClose}
                />
            )}
            {isExpanded && dropdownAvailable && isOpen && (
                <CategoryFilters
                    searchOption={searchOption}
                    targetRef={categoryFilersRef}
                    ref={dropdownRef}
                    onSelect={chooseSearchType}
                    onReset={removeSearchType}
                />
            )}
            {isSearchFilterTooltipVisible && categoryFilersRef?.current && (
                <SearchTooltip
                    targetRef={categoryFilersRef.current}
                    type={searchFilterTooltipType as SearchTooltipTypes}
                    onClose={onSearchFilterTooltipClose}
                />
            )}
        </TransitionWrapper>
    );
});

WebSearch.displayName = 'WebSearch';
